import { default as React, useCallback, useState, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Custom field component for Provider input (used in Terraform Module template)
 * Automatically clears itself when standardTemplate changes away from 'terraform-module'
 */
export const ProviderField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const [value, setValue] = useState(formData || '');
  const [validationError, setValidationError] = useState<string>('');

  // Get templateSource and standardTemplate from form context
  const templateSource = formContext?.formData?.templateSource || '';
  const standardTemplate = formContext?.formData?.standardTemplate || '';

  // Determine if this field should be visible
  const shouldBeVisible =
    templateSource === 'standard' && standardTemplate === 'terraform-module';

  // Handle change - must be declared before early return to follow Rules of Hooks
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      // Convert to lowercase (provider names should be lowercase)
      const lowercaseValue = rawValue.toLowerCase();

      // Validate: lowercase letters and hyphens only, must start and end with letter
      // Pattern prevents trailing hyphens which would create invalid Terraform names
      const pattern = /^[a-z]([a-z-]*[a-z])?$/;
      if (lowercaseValue && !pattern.test(lowercaseValue)) {
        setValidationError(
          'Provider must start and end with a lowercase letter, and contain only lowercase letters and hyphens.',
        );
      } else {
        setValidationError('');
      }

      setValue(lowercaseValue);
      onChange(lowercaseValue || (undefined as unknown as string));
    },
    [onChange],
  );

  // Clear field when it should no longer be visible
  // This prevents stale provider values from persisting in form data
  useEffect(() => {
    if (!shouldBeVisible && formData) {
      setValue('');
      setValidationError('');
      // Cast to unknown first since Backstage's onChange type doesn't permit undefined,
      // but passing undefined is the correct way to clear the field value
      onChange(undefined as unknown as string);
    }
  }, [shouldBeVisible, formData, onChange]);

  // Only render when templateSource is 'standard' AND standardTemplate is 'terraform-module'
  if (!shouldBeVisible) {
    return null;
  }

  const placeholder = uiSchema?.['ui:placeholder'] || 'e.g., gcp, azure, okta';
  const description = uiSchema?.['ui:description'] || schema.description;

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors && rawErrors.length > 0) || !!validationError}
      fullWidth
    >
      <TextField
        id={idSchema?.$id}
        label={schema.title || 'Provider'}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        error={(rawErrors && rawErrors.length > 0) || !!validationError}
        helperText={
          validationError ||
          (rawErrors?.length ? rawErrors.join(', ') : description)
        }
        fullWidth
        inputProps={{
          maxLength: 30,
        }}
      />
    </FormControl>
  );
};

/**
 * Scaffolder field extension for Provider input
 */
export const ProviderFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ProviderField',
    component: ProviderField,
  }),
);
